/**
 * RefundPage.js
 * DataQuad Refund Policy — public page
 */

import React, { useEffect } from 'react';
import { useTheme } from '../utils/ThemeSwitcher';
import { ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RefundPage = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    if (!document.querySelector('link[href*="Outfit"]')) document.head.appendChild(link);
    window.scrollTo(0, 0);
    document.title = 'Refund Policy — Noble Syntax';
  }, []);

  const colors = {
    bg: isDarkMode ? '#08090B' : '#FFFFFF',
    cardBg: isDarkMode ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
    text: isDarkMode ? '#E5E7EB' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    heading: isDarkMode ? '#F9FAFB' : '#111827',
    border: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    accent: '#2B8AFF',
    green: '#10B981',
    amber: '#F59E0B',
  };

  const sectionStyle = { marginBottom: '32px' };
  const h2Style = { fontSize: '20px', fontWeight: '600', color: colors.heading, marginBottom: '12px', marginTop: 0 };
  const pStyle = { fontSize: '15px', lineHeight: '1.75', color: colors.text, margin: '0 0 12px 0' };
  const liStyle = { fontSize: '15px', lineHeight: '1.75', color: colors.text, marginBottom: '6px' };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.bg,
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: isDarkMode ? 'rgba(8,9,11,0.92)' : 'rgba(255,255,255,0.92)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backdropFilter: 'blur(12px)',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              color: colors.accent, fontSize: '14px', fontWeight: '500',
              fontFamily: 'inherit',
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.textSecondary, fontSize: '13px' }}>
            <FileText size={14} />
            <span>Legal</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px 80px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', color: colors.heading, marginBottom: '8px' }}>Refund Policy</h1>
        <p style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '40px' }}>Last updated: January 27, 2026</p>

        <div style={{
          backgroundColor: colors.cardBg,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          padding: '40px',
        }}>
          {/* Quick Summary Box */}
          <div style={{
            padding: '20px 24px',
            borderRadius: '12px',
            backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.06)',
            border: `1px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)'}`,
            marginBottom: '32px',
          }}>
            <p style={{ ...pStyle, fontWeight: '600', color: colors.green, marginBottom: '8px', fontSize: '16px' }}>
              Quick Summary
            </p>
            <p style={{ ...pStyle, marginBottom: 0 }}>
              15-day free trial (no charge). 14-day refund window on all paid subscriptions. One refund per customer. No refunds after 14 days — cancel anytime to stop future billing.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>1. Free Trial</h2>
            <p style={pStyle}>
              All DataQuad subscription plans include a <strong>15-day free trial</strong>. During the trial period,
              you have full access to your chosen plan's features at no cost. If you cancel before the trial ends,
              you will not be charged. No refund is applicable during the trial period as no payment is collected.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>2. 14-Day Refund Window</h2>
            <p style={pStyle}>
              If you are not satisfied with DataQuad after your free trial converts to a paid subscription,
              you may request a <strong>full refund within 14 days</strong> of your first paid charge.
              This applies to all subscription plans (Standard, Pro, and Max) on both monthly and annual billing.
            </p>
            <p style={pStyle}>
              <strong>After the 14-day window, no refunds will be issued.</strong> You may still cancel your
              subscription at any time to prevent future charges. See Section 4 below.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>3. Annual Subscriptions</h2>
            <p style={pStyle}>
              For annual plans, the 14-day refund window applies from the date of the annual charge. If you request
              a refund within 14 days, you will receive a full refund of the annual payment.
              After 14 days, no partial or pro-rated refunds will be issued for annual subscriptions.
              You may cancel at any time and retain access until the end of your paid annual period.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>4. Cancellation vs. Refund</h2>
            <p style={pStyle}>
              Cancellation and refunds are separate actions:
            </p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}>
                <strong>Cancellation</strong> stops future billing. You retain access until the end of your current
                billing period. Cancel any time via your account settings or the Paddle customer portal at{' '}
                <a href="https://paddle.net" style={{ color: colors.accent }}>paddle.net</a>.
              </li>
              <li style={liStyle}>
                <strong>Refund</strong> returns money already charged and <strong>immediately revokes access</strong> to
                paid features. Your account reverts to an inactive state. Full refunds are only
                available within the 14-day refund window.
              </li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>5. Refund Eligibility & Limits</h2>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}><strong>One refund per customer.</strong> If you have previously received a refund for a DataQuad subscription, you are not eligible for another refund on any subsequent subscription.</li>
              <li style={liStyle}>Refunds are only available for the <strong>first-time subscription charge</strong>. Renewal charges are not eligible for refund.</li>
              <li style={liStyle}>Refund eligibility is determined at the sole discretion of Noble Syntax Limited and our Merchant of Record, Paddle.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>6. How to Request a Refund</h2>
            <p style={pStyle}>
              You may request a refund through either of these channels:
            </p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}><strong>Email us</strong> at support@noblesyntax.com with your account email address and reason for the refund request.</li>
              <li style={liStyle}><strong>Contact Paddle directly</strong> via the buyer support portal at{' '}
                <a href="https://paddle.net" style={{ color: colors.accent }}>paddle.net</a>.
              </li>
            </ul>
            <p style={pStyle}>
              All refunds are processed by Paddle, our Merchant of Record. Once approved:
            </p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}>Refunds are returned to the original payment method</li>
              <li style={liStyle}>Processing time is typically 5–10 business days, depending on your bank or card provider</li>
              <li style={liStyle}>You will receive an email confirmation from Paddle when the refund is processed</li>
              <li style={liStyle}>The refunded amount may differ slightly from the original charge due to currency fluctuations or tax adjustments</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>7. Paddle as Merchant of Record</h2>
            <p style={pStyle}>
              Our order process is conducted by our online reseller{' '}
              <a href="https://paddle.com" style={{ color: colors.accent }}>Paddle.com</a>.
              Paddle is the Merchant of Record for all DataQuad orders. Paddle provides payment processing
              and handles returns. You can learn more about Paddle's refund and dispute policy in{' '}
              <a href="https://www.paddle.com/legal/checkout-buyer-terms" style={{ color: colors.accent }}>
                Paddle's Checkout Buyer Terms and Conditions
              </a>.
            </p>
          </div>

          <div style={{ ...sectionStyle, marginBottom: 0 }}>
            <h2 style={h2Style}>8. Contact Us</h2>
            <p style={pStyle}>For refund requests or questions about this policy:</p>
            <p style={{ ...pStyle, marginBottom: 0 }}>
              <strong>Noble Syntax Limited</strong><br />
              Email: support@noblesyntax.com<br />
              Website: dataquadapp.com<br />
              Buyer support: <a href="https://paddle.net" style={{ color: colors.accent }}>paddle.net</a><br />
              Location: Lusaka, Zambia
            </p>
          </div>
        </div>
      </div>

      <style>{`
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}; }
      `}</style>
    </div>
  );
};

export default RefundPage;